import { useState, useEffect } from 'react';
import { Save, RotateCcw, AlertCircle, Loader2 } from 'lucide-react';
import { Heading, Skeleton } from '@aaimplatform/ui';
import { usePlatformConfig } from '../../hooks/usePlatformConfig';

const SECURITY_DEFAULTS = {
  requireMfa: true,
  sessionTimeout: 30,
  passwordExpiry: 90,
  failedLoginAttempts: 5,
  ipRestrictions: false,
  ipAllowlist: '',
};

type SecuritySettings = typeof SECURITY_DEFAULTS;

/**
 * Security Configuration - Platform Admin
 *
 * Authentication, MFA, and access control settings
 */
export function SecurityConfigPage() {
  const { config, isLoading, error, save, isSaving, saveError } =
    usePlatformConfig<SecuritySettings>('security');
  const [hasChanges, setHasChanges] = useState(false);
  const [settings, setSettings] = useState<SecuritySettings>(SECURITY_DEFAULTS);

  useEffect(() => {
    if (config) {
      setSettings({ ...SECURITY_DEFAULTS, ...config });
      setHasChanges(false);
    }
  }, [config]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    save(settings);
    setHasChanges(false);
  };

  const handleReset = () => {
    if (config) setSettings(config);
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-end gap-3 mb-6">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-36" />
        </div>
        <Skeleton className="h-64 rounded-sm mb-6" />
        <Skeleton className="h-48 rounded-sm" />
      </div>
    );
  }

  return (
    <div>
      {/* Action Buttons */}
      <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
        <button
          onClick={handleReset}
          disabled={!hasChanges}
          className={`flex items-center gap-2 px-4 py-2.5 border border-recessed rounded-sm font-sans text-sm font-medium transition-colors ${
            hasChanges ? 'text-primary hover:bg-elevated' : 'text-tertiary cursor-not-allowed'
          }`}
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-sm font-sans text-sm font-medium transition-colors ${
            hasChanges && !isSaving
              ? 'bg-accent text-white hover:bg-accent-hover'
              : 'bg-recessed text-tertiary cursor-not-allowed'
          }`}
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Error State */}
      {(error || saveError) && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {error ? 'Failed to load configuration' : 'Failed to save changes'}
          </div>
        </div>
      )}

      {/* Authentication Settings */}
      <div className="bg-surface rounded-sm shadow-sm p-6 mb-6">
        <Heading level={3} className="mb-6">
          Authentication Settings
        </Heading>

        <div className="space-y-6">
          <div className="flex items-center justify-between py-3 border-b border-recessed">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Require MFA for all users
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                Enforce multi-factor authentication platform-wide
              </div>
            </div>
            <button
              onClick={() => handleChange('requireMfa', !settings.requireMfa)}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.requireMfa ? 'bg-accent' : 'bg-recessed'
              }`}
            >
              <div
                className={`w-5 h-5 bg-surface rounded-full shadow transition-transform ${
                  settings.requireMfa ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-recessed">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Session timeout (minutes)
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                Auto-logout after inactivity
              </div>
            </div>
            <input
              type="number"
              value={settings.sessionTimeout}
              onChange={e => handleChange('sessionTimeout', parseInt(e.target.value))}
              className="w-24 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent"
            />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-recessed">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Password expiry (days)
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                Force password reset after period
              </div>
            </div>
            <input
              type="number"
              value={settings.passwordExpiry}
              onChange={e => handleChange('passwordExpiry', parseInt(e.target.value))}
              className="w-24 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent"
            />
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Failed login attempts before lockout
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                Account lockout threshold
              </div>
            </div>
            <input
              type="number"
              value={settings.failedLoginAttempts}
              onChange={e => handleChange('failedLoginAttempts', parseInt(e.target.value))}
              className="w-24 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent"
            />
          </div>
        </div>
      </div>

      {/* IP Allowlist */}
      <div className="bg-surface rounded-sm shadow-sm p-6">
        <Heading level={3} className="mb-6">
          IP Allowlist
        </Heading>

        <div className="flex items-center justify-between py-3 border-b border-recessed mb-4">
          <div>
            <div className="text-sm font-sans font-medium text-primary leading-normal">
              Enable IP restrictions
            </div>
            <div className="text-xs font-sans text-tertiary leading-normal mt-1">
              Only allow access from specified IP ranges
            </div>
          </div>
          <button
            onClick={() => handleChange('ipRestrictions', !settings.ipRestrictions)}
            className={`w-12 h-6 rounded-full transition-colors ${
              settings.ipRestrictions ? 'bg-accent' : 'bg-recessed'
            }`}
          >
            <div
              className={`w-5 h-5 bg-surface rounded-full shadow transition-transform ${
                settings.ipRestrictions ? 'translate-x-6' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        <textarea
          value={settings.ipAllowlist}
          onChange={e => handleChange('ipAllowlist', e.target.value)}
          disabled={!settings.ipRestrictions}
          placeholder="Enter IP ranges (one per line)&#10;e.g., 192.168.1.0/24"
          className={`w-full h-32 px-4 py-3 border border-recessed rounded-sm text-sm font-mono focus:outline-none focus:border-accent resize-none ${
            !settings.ipRestrictions ? 'bg-elevated text-tertiary' : ''
          }`}
        />
      </div>
    </div>
  );
}
